/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Image,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import ConsteedButton from '@consteed/frontend/components/pageDesigner/ConsteedButton'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

/**
 * LandingPageHero component
 *
 * @param {object} props
 * @param {string} - props.videoURL - video URL
 * @param {image} - props.placeholderImage - placeholder image for video
 * @param {boolean} - props.autoplay - autoplay flag for video
 * @param {boolean} - props.showControls - should the video controls be displayed or not
 * @param {boolean} - props.showLogo - should consteed logo be displayed or not
 * @param {string} - props.overlayAlignmentX - horizontal alignment for overlay (left | center)
 * @param {string} - props.overlayAlignmentY - vertical alignment for overlay (top | bottom | center)
 * @param {string} - props.backgroundTheme - defines the theme of the background (which has impact on the text color of the overlay)
 * @param {array} - props.regions - array of pagedesigner components (RTE or Button)
 * @returns {React.ReactElement} - LandingPageHero component.
 */
export const LandingPageHero = ({
    videoURL,
    videoPortraitURL,
    placeholderImage,
    autoplay = false,
    loop = false,
    showControls = false,
    showLogo = false,
    overlayAlignmentX = 'left',
    overlayAlignmentY = 'center',
    backgroundTheme,
    regions
}) => {
    const logoReduced = getAssetUrl('static/img/global/logoReduced.png')
    const textColor = backgroundTheme === 'light' ? 'black' : 'white'
    // backgrounds that are not light or dark will get a filter so they are dark
    const darken = ['light', 'dark'].indexOf(backgroundTheme) === -1
    const styles = useMultiStyleConfig('LandingPageHero', {
        overlayAlignmentX,
        overlayAlignmentY,
        textColor
    })

    return (
        <Box className={'pdcOuterBox pdcFullWidth'}>
            <Box className="videoContainer" {...styles.videoContainer}>
                <video
                    autoPlay={autoplay}
                    loop={loop}
                    poster={placeholderImage?.url}
                    controls={showControls}
                    muted
                    style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                    playsInline={true}
                >
                    {videoPortraitURL && (
                        <source
                            src={videoPortraitURL}
                            type="video/mp4"
                            media="(orientation: portrait)"
                        />
                    )}
                    <source src={videoURL || videoPortraitURL} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                {darken && <Box {...styles.darkenedBg} />}
            </Box>
            <Flex className="overlayContainer" {...styles.overlayContainer}>
                {showLogo && (
                    <Flex pl={4} alignItems="center" gap={2}>
                        <Image src={logoReduced} alt="Logo" width={{base: '56px', xl: '98px'}} />
                        <Text
                            as="strong"
                            color="yellow.400"
                            textStyle={{base: 'light2Xlarge', xl: 'light5Xlarge'}}
                        >
                            <Text as="span" fontWeight="500">
                                con
                            </Text>
                            steed
                        </Text>
                    </Flex>
                )}
                {regions[0]?.components?.map((component) => {
                    if (component?.typeId === 'commerce_assets.editorialRichText') {
                        return (
                            <Box
                                maxWidth={{lg: '50%'}}
                                m="0"
                                textAlign={overlayAlignmentX}
                                key={component.id}
                            >
                                <RTE
                                    richText={component.data.richText}
                                    textStyles={{base: 'light2Xlarge', xl: 'light5Xlarge'}}
                                />
                            </Box>
                        )
                    } else if (component?.typeId === 'custom_assets.button') {
                        return (
                            <ConsteedButton
                                buttonText={component.data.buttonText}
                                buttonLink={component.data.buttonLink}
                                key={component.id}
                                pX={4}
                            />
                        )
                    }
                })}
            </Flex>
        </Box>
    )
}

LandingPageHero.propTypes = {
    videoURL: PropTypes.string,
    videoPortraitURL: PropTypes.string,
    placeholderImage: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    showControls: PropTypes.bool,
    showLogo: PropTypes.bool,
    overlayAlignmentX: PropTypes.oneOf(['left', 'center']),
    overlayAlignmentY: PropTypes.oneOf(['top', 'bottom', 'center']),
    backgroundTheme: PropTypes.oneOf(['light', 'medium', 'dark']),
    regions: PropTypes.array
}

export default LandingPageHero
